import { useMutation, useQuery } from "@apollo/client";
import EditOutlined from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import {
  type CredentialImplementationForStaffQuery,
  ImplementationStatus,
  type UpdateCredentialImplementationInput,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CredentialDialog } from "routes/Customer/Credentials/CredentialDialog";

import {
  CREDENTIAL_FOR_PORTAL_LOGIN_QUERY,
  CREDENTIAL_IMPLEMENTATION_FOR_STAFF_QUERY,
  UPDATE_CREDENTIAL_IMPLEMENTATION,
} from "../api";
import { CommentsSection } from "./CommentsSection";
import { drawerSectionStackSpacing } from "./DrawerSection";
import { EditableFields, rowToFormState, type EditableFormState } from "./EditableFields";
import { ImplementationStatusChip } from "./ImplementationStatusChip";
import { RowSummary } from "./RowSummary";

type Row = NonNullable<CredentialImplementationForStaffQuery["credentialImplementationForStaff"]>;

function formsEqual(a: EditableFormState, b: EditableFormState) {
  return (
    a.implementationStatus === b.implementationStatus &&
    (a.assignee?.id ?? null) === (b.assignee?.id ?? null) &&
    a.notes === b.notes &&
    a.blockedReason === b.blockedReason
  );
}

/**
 * Build a patch payload that only includes fields the user actually changed
 * relative to the loaded row. Sending fields the user didn't touch would let a
 * stale form clobber concurrent edits (e.g. a teammate flipping status to
 * COMPLETE while this drawer was open). The resolver mirrors this contract by
 * only writing fields the input explicitly carries.
 *
 * Special case for blockedReason: when status is changing, we always send the
 * derived reason so the resolver applies the BLOCKED <-> blockedReason
 * invariant against the new status. When status is unchanged but the reason
 * itself was edited (only possible in the BLOCKED state), we send just the
 * reason.
 */
function buildUpdateInput(
  form: EditableFormState,
  baseline: EditableFormState
): UpdateCredentialImplementationInput {
  const input: UpdateCredentialImplementationInput = {};

  const statusChanged = form.implementationStatus !== baseline.implementationStatus;
  if (statusChanged) {
    input.implementationStatus = form.implementationStatus;
    input.blockedReason =
      form.implementationStatus === ImplementationStatus.Blocked ? form.blockedReason : null;
  } else if (
    form.implementationStatus === ImplementationStatus.Blocked &&
    form.blockedReason !== baseline.blockedReason
  ) {
    input.blockedReason = form.blockedReason;
  }

  if ((form.assignee?.id ?? null) !== (baseline.assignee?.id ?? null)) {
    input.assigneeId = form.assignee?.id ?? null;
  }

  if (form.notes !== baseline.notes) {
    input.notes = form.notes;
  }

  return input;
}

export function CredentialImplementationDrawer({
  implementationId,
  open,
  onClose,
}: {
  implementationId: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const { user: currentUser } = useCurrentUser();
  const { data, loading, error, refetch } = useQuery(CREDENTIAL_IMPLEMENTATION_FOR_STAFF_QUERY, {
    variables: { id: implementationId ?? "" },
    skip: !implementationId,
    // Avoid writing drawer-only detail fields into the normalized cache for
    // the list row entity. Those broadcasts repaint the grid on first open of
    // each row.
    fetchPolicy: "no-cache",
  });

  const row = data?.credentialImplementationForStaff ?? undefined;
  const credentialId = row?.credentialId ?? undefined;
  const [editorOpen, setEditorOpen] = useState(false);

  // Login payload for the "Open with Encampy" launcher. Password is gated
  // server-side by ViewCredentials, so a staff user without it gets a blank
  // password and the launcher stays disabled.
  const { data: loginData, refetch: refetchLogin } = useQuery(CREDENTIAL_FOR_PORTAL_LOGIN_QUERY, {
    variables: { id: credentialId ?? "" },
    skip: !credentialId,
  });
  const loginCredential = loginData?.credential ?? null;
  const [baseline, setBaseline] = useState<EditableFormState | null>(null);
  const [form, setForm] = useState<EditableFormState | null>(null);

  // Re-hydrate the form only when the open row's identity changes (drawer
  // opens, or user clicks a different row). We intentionally do NOT depend on
  // the whole `row` object: cache updates that change *fields* of the same row
  // (e.g. `CommentsSection` writing the new comment + `mostRecentComment` back
  // into the cache) would otherwise clobber the user's in-progress edits to
  // status / assignee / notes / blockedReason.
  useEffect(() => {
    if (!row) {
      setBaseline(null);
      setForm(null);
      return;
    }
    const next = rowToFormState(row);
    setBaseline(next);
    setForm(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  const dirty = useMemo(() => {
    if (!form || !baseline) return false;
    return !formsEqual(form, baseline);
  }, [form, baseline]);

  const saveBlocked =
    form?.implementationStatus === ImplementationStatus.Blocked &&
    !(form.blockedReason?.trim() ?? "");

  const [updateCi, { loading: saving }] = useMutation(UPDATE_CREDENTIAL_IMPLEMENTATION);

  const handleSave = useCallback(async () => {
    if (!implementationId || !form || !baseline) return;
    try {
      await updateCi({
        variables: {
          id: implementationId,
          input: buildUpdateInput(form, baseline),
        },
      });
      setBaseline(form);
      alerts.success("Saved");
      onClose();
    } catch (e) {
      alerts.error("Could not save changes", e);
    }
  }, [alerts, baseline, form, implementationId, onClose, updateCi]);

  const handleCancel = useCallback(() => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them and close the drawer?")) {
      return;
    }
    if (baseline) setForm(baseline);
    onClose();
  }, [baseline, dirty, onClose]);

  // After editing the shared credential, re-pull the implementation row (its
  // credentialName mirrors the credential username) and the login payload.
  const handleEditorClose = useCallback(() => {
    setEditorOpen(false);
    void refetch();
    if (credentialId) void refetchLogin();
  }, [credentialId, refetch, refetchLogin]);

  const credentialActions = credentialId ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        startIcon={<EditOutlined fontSize="small" />}
        onClick={() => setEditorOpen(true)}
      >
        Edit credential
      </Button>
      <EncampyPortalLoginLauncher
        portalCredential={{ credential: loginCredential }}
        credentialId={credentialId}
        onSuccess={(message) => alerts.success(message)}
        onError={(message) => alerts.error(message)}
        size="small"
        disabled={!loginCredential?.username || !loginCredential?.password}
        disabledReason="Portal credentials are required for portal login"
      />
    </Stack>
  ) : undefined;

  const titleText = row
    ? [row.tenantName, row.facilityName, row.portalJurisdiction].filter(Boolean).join(" · ")
    : "Credential implementation";

  const drawerTitle =
    implementationId && row && form ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          noWrap
          title={titleText}
          sx={{ minWidth: 0, flex: "1 1 auto", fontWeight: 600, lineHeight: 1.3 }}
        >
          {titleText}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <ImplementationStatusChip status={form.implementationStatus} />
          {row.sharedCredentialImplementationCount > 0 ? (
            <Tooltip
              title={`Shared with ${row.sharedCredentialImplementationCount} other facilit${
                row.sharedCredentialImplementationCount === 1 ? "y" : "ies"
              } in Encamp (same login across sites).`}
            >
              <Chip
                size="small"
                icon={<LinkIcon fontSize="small" />}
                label={`+${row.sharedCredentialImplementationCount}`}
              />
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    ) : implementationId ? (
      titleText
    ) : undefined;

  const facilityHref = row && form ? `/o/${row.tenantId}/facilities/${row.facilityId}` : undefined;

  return (
    <>
      <RightDrawerShell
        open={open}
        onClose={handleCancel}
        hideBackdrop
        showContent={!!implementationId}
        title={drawerTitle}
        headerActions={
          facilityHref ? (
            <Tooltip title="Open facility">
              <IconButton
                component="a"
                href={facilityHref}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="Open facility in new tab"
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
        footer={
          implementationId ? (
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleSave()}
                disabled={saving || !dirty || saveBlocked || !form}
              >
                {saving ? <CircularProgress size={22} color="inherit" /> : "Save"}
              </Button>
            </Stack>
          ) : undefined
        }
      >
        {loading && !row ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Typography color="error">Could not load this record.</Typography>
        ) : row && form ? (
          <Stack spacing={drawerSectionStackSpacing} divider={<Divider flexItem />}>
            <RowSummary row={row} credentialActions={credentialActions} />
            <EditableFields form={form} onChange={setForm} />
            <CommentsSection
              credentialImplementationId={row.id}
              comments={row.comments}
              currentUserId={currentUser?.id}
            />
          </Stack>
        ) : null}
      </RightDrawerShell>
      {editorOpen && credentialId && row ? (
        <CredentialDialog
          mode="edit"
          credentialId={credentialId}
          tenantId={row.tenantId}
          onClose={handleEditorClose}
        />
      ) : null}
    </>
  );
}
